// The "what next" card over a finished board (games/_finish.html.erb). Every game reports progress through
// lib/stats.ts, which sends dt:progress; when a game becomes won (now, or restored as won from a save) the card
// offers a new game, more games, or a look at the finished board. It hides again as soon as a new game starts.
// On today's daily it also says how many days in a row that makes (lib/streak.ts), from two days up.
import { localISO, pageDate, records } from "./daily"
import { playedDays, runLength } from "./streak"
import { cardWords, drawCard, fill, resultSubject, resultText, wire, type Result } from "./share"

/** "That's 3 days in a row." for today's daily (this finish counts, even before the game records it), else "". */
function runNote(): string {
  const board = document.querySelector<HTMLElement>("#board[data-daily-game]")
  if (!board || board.dataset.dailyArchive === "true") return ""
  const today = localISO()
  const days = playedDays(records())
  days.add(today)
  const run = runLength(days, today)
  return run >= 2 ? `That's ${run} days in a row.` : ""
}

export function initFinish(): void {
  const panel = document.querySelector<HTMLElement>("[data-finish]")
  if (!panel) return
  let won = false
  // Focus moves to "New game" only for a win the player just made, not for a finished game reopened later.
  let acted = false
  const mark = (): void => { acted = true }
  addEventListener("pointerdown", mark, { capture: true, once: true })
  addEventListener("keydown", mark, { capture: true, once: true })
  const note = panel.querySelector<HTMLElement>("[data-finish-note]")
  const primary = panel.querySelector<HTMLElement>(".btn-primary")

  const statText = (name: string): string => {
    const el = document.querySelector<HTMLElement>(`[data-stat="${name}"]`)
    return el && el.offsetParent !== null ? el.textContent ?? "" : ""
  }
  const show = (fresh: boolean): void => {
    panel.hidden = false
    const parts = [statText("score") && `${statText("score")} right`, statText("time") && `Time ${statText("time")}`, statText("moves") && `${statText("moves")} moves`].filter(Boolean)
    if (note) note.textContent = [parts.join(" · "), runNote()].filter(Boolean).join(" ")
    if (fresh && acted) setTimeout(() => primary?.focus({ preventScroll: true }), 50)
  }
  const main = panel.querySelector<HTMLElement>("[data-finish-main]")
  const view = panel.querySelector<HTMLElement>("[data-finish-share-view]")
  const hide = (): void => { panel.hidden = true; back() }

  // "Share my result": the card swaps to the share view, with the score card picture and a button per way to send.
  let picture: File | null = null
  let pictureUrl = ""
  const result = (): { r: Result; url: string } => {
    const d = panel.dataset
    const board = document.querySelector<HTMLElement>("#board")
    const daily = board?.dataset.dailyGame ? pageDate(board) : null
    const deal = document.querySelector<HTMLElement>("[data-share-deal]")?.dataset.shareDeal
    const r: Result = {
      name: d.shareName ?? "", what: d.shareWhat ?? "", verb: d.shareVerb ?? "won",
      time: statText("time") || undefined, moves: statText("moves") || undefined, score: statText("score") || undefined,
      daily: daily ? new Date(`${daily.date}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" }) : undefined,
      deal: daily ? undefined : deal,
      // A game that can be replayed exactly (a trivia round) gives its own link query and a spoiler-free result line.
      grid: board?.dataset.shareGrid || undefined,
      same: !!board?.dataset.shareQuery?.includes("round="),
    }
    const query = board?.dataset.shareQuery
    const base = daily ? `${d.sharePath}/daily/${daily.date}` : r.deal ? `${d.sharePath}?deal=${r.deal}` : d.sharePath ?? ""
    const path = query ? `${base}${base.includes("?") ? "&" : "?"}${query}` : base
    return { r, url: `${d.shareOrigin}${path}` }
  }
  const openShare = (): void => {
    if (!view || !main) return
    const { r, url } = result()
    const text = resultText(r)
    fill(view, text, url, resultSubject(r))
    const preview = view.querySelector<HTMLElement>("[data-share-preview]")
    if (preview) preview.textContent = text
    main.hidden = true
    view.hidden = false
    view.querySelector<HTMLElement>("[data-finish-share-title]")?.focus({ preventScroll: true })
    const img = view.querySelector<HTMLImageElement>("[data-share-card]")
    const save = view.querySelector<HTMLAnchorElement>('[data-share-channel="picture"]')
    const words = cardWords(r, new URL(panel.dataset.shareOrigin ?? location.origin).host)
    void drawCard(words, panel.dataset.shareArt ?? "").then((blob) => {
      if (!blob || view.hidden) return
      if (pictureUrl) URL.revokeObjectURL(pictureUrl)
      pictureUrl = URL.createObjectURL(blob)
      picture = new File([blob], "card-and-puzzle-games-result.png", { type: "image/png" })
      if (img) {
        img.src = pictureUrl
        img.alt = `Score card: ${[words.title, words.sub, words.big, words.small].filter(Boolean).join(", ")}`
        img.hidden = false
      }
      if (save) { save.href = pictureUrl; save.hidden = false }
    })
  }
  function back(): void {
    if (!view || !main || view.hidden) return
    view.hidden = true
    main.hidden = false
  }
  if (view) wire(view, () => picture)
  const shareBtn = panel.querySelector<HTMLElement>("[data-finish-share]")
  if (shareBtn) {
    shareBtn.hidden = false
    shareBtn.addEventListener("click", openShare)
  }
  panel.querySelector("[data-finish-share-back]")?.addEventListener("click", () => {
    back()
    shareBtn?.focus({ preventScroll: true })
  })

  document.addEventListener("dt:progress", (e) => {
    const d = (e as CustomEvent<{ won: boolean; fresh: boolean }>).detail
    if (d.won && !won) show(d.fresh)
    if (!d.won) hide()
    won = d.won
  })
  panel.querySelector("[data-finish-new]")?.addEventListener("click", () => {
    // The game's own New game button, so its rules (and any between-game ad break) apply unchanged.
    document.querySelector<HTMLElement>('[data-act="new"]')?.click()
  })
  // Back to something on the board that takes focus (the board itself doesn't), so arrows and Ctrl+Z keep
  // working after "Look at the board" (bug hunt round 2); else the Undo button.
  const close = (): void => {
    hide()
    const target = document.querySelector<HTMLElement>('#board [data-nav]:not([disabled]), #board [tabindex="0"], #board button:not([disabled])')
      ?? document.querySelector<HTMLElement>('[data-act="undo"]')
    target?.focus({ preventScroll: true })
  }
  panel.querySelector("[data-finish-close]")?.addEventListener("click", close)
  // A tap on the dimmed backdrop, outside the card, closes it too.
  panel.addEventListener("click", (e) => { if (e.target === panel) close() })
  panel.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return
    if (view && !view.hidden) { back(); shareBtn?.focus({ preventScroll: true }) } else close()
  })
}
